/**
 * notifications.ts
 *
 * Centralised in-app notification service for CBOps.
 *
 * Every user-facing action (leave submitted, task assigned, etc.) calls one of
 * the named helpers here so the logic lives in one place and is easy to extend.
 *
 * All helpers write one row to the `notifications` table. Supabase Realtime
 * then delivers it instantly to the recipient's browser via NotificationContext.
 *
 * Usage:
 *   import { notifyLeaveSubmitted } from '../lib/notifications';
 *   await notifyLeaveSubmitted({ actorId, actorName, managerId, leaveId, leaveType, startDate });
 *
 * Core send function:
 *   sendNotification(payload) — low-level, used by all helpers.
 *
 * Roadmap rule 5: "Every action must trigger a notification to the right person."
 */

import { supabase } from './supabase';
import { api, apiConfigured, getCurrentWorkspaceId, NO_WORKSPACE_MESSAGE } from './apiClient';
import type { NotificationType } from '../types';

// ─── Core send ────────────────────────────────────────────────────────────────

/**
 * Outcome of a send. Notifications are best-effort — nothing in this module ever
 * throws, and a caller that ignores the return value behaves exactly as before.
 * Every `notify*` helper now RETURNS this rather than `Promise<void>`, so a caller
 * that cares can finally tell "the decision landed AND the person was told" apart
 * from "the decision landed". Previously the helpers discarded the result of their
 * own send, making that distinction unavailable to anyone.
 *
 * `ok: false` is deliberately broader than "the insert failed". It also covers a
 * send that succeeded into an untrustworthy recipient list — see RESOLUTION_FAILED.
 * The honest claim this type makes is "the right people were notified", not
 * "a row was written".
 */
export type SendResult = { ok: boolean; error?: string };

/**
 * Log a failed lookup with every PostgREST diagnostic field.
 *
 * `message` alone is routinely useless here — an RLS refusal arrives as a bare
 * "JSON object requested, multiple (or no) rows returned", while `code`
 * ("PGRST116") and `hint` are what actually name the cause. The raw `error` is
 * kept as a last field so nothing is lost to a field this shape doesn't know about.
 */
function logLookupFailure(
  where: string,
  what: string,
  error: { message?: string; code?: string; details?: string; hint?: string } | null,
): void {
  console.error(`[notifications] ${where}: ${what} lookup failed`, {
    message: error?.message,
    code:    error?.code,
    details: error?.details,
    hint:    error?.hint,
    error,
  });
}

/**
 * A recipient-resolution cascade that could not be trusted.
 *
 * Every multi-step resolver below (manager → department head → super_admins)
 * advances on `recipients.size === 0`. A FAILED lookup produces an empty set
 * exactly like a legitimately empty one, so the fallback fires and the alert
 * silently goes to the wrong audience — or, if the last step also fails, to
 * nobody. Callers get `ok: false` in that case even when a send did happen,
 * because "we notified someone" is not the same claim as "we notified the
 * right someone", and only the caller can decide whether that matters.
 */
const RESOLUTION_FAILED = 'Recipient lookup failed — the notification may have reached the wrong people, or nobody.';

export interface NotificationPayload {
  /** The recipient user (auth.users.id / profiles.id). */
  userId:     string;
  /** The user who triggered the action (for display). */
  actorId?:   string;
  type:       NotificationType;
  title:      string;
  body?:      string;
  entityType?: string;
  entityId?:   string;
  /** Deep-link path inside the app, e.g. "/hr/leaves". */
  link?:       string;
  priority?:   'low' | 'normal' | 'high' | 'critical';
  /**
   * Stable idempotency key for fan-out / repeated sends. When set, the DB
   * unique index `(user_id, type, dedupe_key)` enforces one row per recipient
   * per key via ON CONFLICT DO NOTHING — so repeated sends (multiple viewers,
   * multiple page loads, multiple days) collapse to a single row. Leave
   * undefined for ordinary one-shot notifications (NULL keys never conflict).
   */
  dedupeKey?:  string;
}

/**
 * Inserts one notification row.
 *
 * Phase-2 routing: when `REACT_APP_API_BASE_URL` is set, prefers the
 * FastAPI backend (`POST /notifications`). On any non-2xx OR network
 * error, falls back to the direct Supabase insert so the existing
 * realtime subscription still receives the row. This dual-path is what
 * makes the cutover safe — flipping the env var enables backend writes
 * with zero downtime, and switching it back disables them just as fast.
 *
 * Errors are logged but never thrown — notifications are best-effort.
 */
export async function sendNotification(payload: NotificationPayload): Promise<SendResult> {
  // Phase-2 path
  if (apiConfigured()) {
    const res = await api.post('/notifications', {
      user_id:     payload.userId,
      actor_id:    payload.actorId ?? null,
      type:        payload.type,
      title:       payload.title,
      body:        payload.body ?? null,
      entity_type: payload.entityType ?? null,
      entity_id:   payload.entityId ?? null,
      link:        payload.link ?? null,
      priority:    payload.priority ?? 'normal',
      dedupe_key:  payload.dedupeKey ?? null,
    });
    if (res.ok) return { ok: true };
    // 401 / 503 / network error — log, then fall back so the user
    // doesn't lose the notification.
    console.warn(
      '[notifications] backend insert failed (status=%d): %s — falling back to Supabase',
      res.status,
      res.error,
    );
  }

  // Phase-1 / fallback path
  // A notification with no company cannot be delivered to the right people. Report it
  // rather than filing it under a guessed one.
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { ok: false, error: NO_WORKSPACE_MESSAGE };
  const row = {
    workspace_id: workspaceId,
    user_id:      payload.userId,
    actor_id:     payload.actorId ?? null,
    type:         payload.type,
    title:        payload.title,
    body:         payload.body ?? null,
    entity_type:  payload.entityType ?? null,
    entity_id:    payload.entityId ?? null,
    link:         payload.link ?? null,
    priority:     payload.priority ?? 'normal',
    dedupe_key:   payload.dedupeKey ?? null,
  };
  // When a dedupe_key is set, let the DB unique index collapse repeats
  // (ON CONFLICT DO NOTHING). Keyless rows insert normally (NULLs never conflict).
  const { error } = payload.dedupeKey
    ? await supabase.from('notifications').upsert(row, {
        onConflict: 'user_id,type,dedupe_key',
        ignoreDuplicates: true,
      })
    : await supabase.from('notifications').insert(row);

  if (error) {
    logLookupFailure('sendNotification', `insert for user ${payload.userId}`, error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Send the same notification to multiple recipients at once.
 *
 * Phase-2 path: `POST /notifications/bulk` with the recipient list. The
 * backend dedupes ids and inserts in one round-trip; the per-user
 * realtime channel still fires per-row so toasts/bell sync correctly.
 *
 * Phase-1 fallback: bulk-insert via the Supabase client. Same behaviour
 * as the original.
 */
export async function sendNotificationToMany(
  userIds: string[],
  base: Omit<NotificationPayload, 'userId'>
): Promise<SendResult> {
  if (!userIds.length) return { ok: true };   // nothing to send is success, not failure

  // Phase-2 path
  if (apiConfigured()) {
    const res = await api.post('/notifications/bulk', {
      user_ids:    userIds,
      actor_id:    base.actorId ?? null,
      type:        base.type,
      title:       base.title,
      body:        base.body ?? null,
      entity_type: base.entityType ?? null,
      entity_id:   base.entityId ?? null,
      link:        base.link ?? null,
      priority:    base.priority ?? 'normal',
      dedupe_key:  base.dedupeKey ?? null,
    });
    if (res.ok) return { ok: true };
    console.warn(
      '[notifications] backend bulk insert failed (status=%d): %s — falling back to Supabase',
      res.status,
      res.error,
    );
  }

  // Phase-1 fallback path
  const workspaceId = getCurrentWorkspaceId();
  if (!workspaceId) return { ok: false, error: NO_WORKSPACE_MESSAGE };
  const rows = userIds.map(userId => ({
    workspace_id: workspaceId,
    user_id:      userId,
    actor_id:     base.actorId ?? null,
    type:         base.type,
    title:        base.title,
    body:         base.body ?? null,
    entity_type:  base.entityType ?? null,
    entity_id:    base.entityId ?? null,
    link:         base.link ?? null,
    priority:     base.priority ?? 'normal',
    dedupe_key:   base.dedupeKey ?? null,
  }));

  // When a dedupe_key is set, the DB unique index (user_id, type, dedupe_key)
  // enforces one row per recipient per key via ON CONFLICT DO NOTHING — making
  // fan-out idempotent across viewers AND days (this replaces the old
  // per-browser localStorage guard). Keyless sends insert as before: NULL
  // dedupe_key never conflicts, so unrelated notification flows are unaffected.
  // (Rows in one batch differ by user_id, so they never conflict with each other.)
  const { error } = base.dedupeKey
    ? await supabase.from('notifications').upsert(rows, {
        onConflict: 'user_id,type,dedupe_key',
        ignoreDuplicates: true,
      })
    : await supabase.from('notifications').insert(rows);
  if (error) {
    logLookupFailure('sendNotificationToMany', `bulk insert for ${userIds.length} recipient(s)`, error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

// ─── HR: Leave notifications ──────────────────────────────────────────────────

/**
 * Employee submits a leave request → notify their manager.
 */
export async function notifyLeaveSubmitted(args: {
  actorId:   string;
  actorName: string;
  managerId: string;
  leaveId:   string;
  leaveType: string;
  startDate: string;
}): Promise<SendResult> {
  return sendNotification({
    userId:     args.managerId,
    actorId:    args.actorId,
    type:       'leave_submitted',
    title:      `${args.actorName} requested leave`,
    body:       `${args.leaveType} starting ${new Date(args.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}. Awaiting your approval.`,
    entityType: 'leave_request',
    entityId:   args.leaveId,
    link:       '/hr/team-leave',
    priority:   'high',
  });
}

/**
 * Manager or HR approves a leave request → notify the employee.
 */
export async function notifyLeaveApproved(args: {
  actorId:    string;
  actorName:  string;
  employeeId: string;
  leaveId:    string;
  leaveType:  string;
  startDate:  string;
  stage:      'manager' | 'hr';   // which approval stage
}): Promise<SendResult> {
  const stageLabel = args.stage === 'hr' ? 'Final approval' : 'Manager approval';
  return sendNotification({
    userId:     args.employeeId,
    actorId:    args.actorId,
    type:       'leave_approved',
    title:      `Your leave was approved`,
    body:       `${stageLabel} by ${args.actorName}. ${args.leaveType} from ${new Date(args.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.`,
    entityType: 'leave_request',
    entityId:   args.leaveId,
    link:       '/hr/my-leave',
  });
}

/**
 * Manager or HR rejects a leave request → notify the employee.
 */
export async function notifyLeaveRejected(args: {
  actorId:    string;
  actorName:  string;
  employeeId: string;
  leaveId:    string;
  leaveType:  string;
  reason?:    string;
}): Promise<SendResult> {
  return sendNotification({
    userId:     args.employeeId,
    actorId:    args.actorId,
    type:       'leave_rejected',
    title:      `Your leave request was declined`,
    body:       args.reason
      ? `Reason: ${args.reason}`
      : `Declined by ${args.actorName}.`,
    entityType: 'leave_request',
    entityId:   args.leaveId,
    link:       '/hr/my-leave',
    priority:   'high',
  });
}

// ─── Work: Task notifications ─────────────────────────────────────────────────

/**
 * Task assigned to a user → notify the assignee.
 */
export async function notifyTaskAssigned(args: {
  actorId:    string;
  actorName:  string;
  assigneeId: string;
  taskId:     string;
  taskTitle:  string;
  projectName?: string;
}): Promise<SendResult> {
  return sendNotification({
    userId:     args.assigneeId,
    actorId:    args.actorId,
    type:       'task_assigned',
    title:      `Task assigned to you`,
    body:       `"${args.taskTitle}"${args.projectName ? ` in ${args.projectName}` : ''} by ${args.actorName}.`,
    entityType: 'task',
    entityId:   args.taskId,
    link:       '/work/tasks',
  });
}

/**
 * Task marked complete → notify the reporter.
 */
export async function notifyTaskCompleted(args: {
  actorId:    string;
  actorName:  string;
  reporterId: string;
  taskId:     string;
  taskTitle:  string;
}): Promise<SendResult> {
  if (args.reporterId === args.actorId) return { ok: true };   // don't notify self
  return sendNotification({
    userId:     args.reporterId,
    actorId:    args.actorId,
    type:       'task_completed',
    title:      `Task completed`,
    body:       `"${args.taskTitle}" was marked complete by ${args.actorName}.`,
    entityType: 'task',
    entityId:   args.taskId,
    link:       '/work/tasks',
  });
}

// ─── Finance: Expense notifications ──────────────────────────────────────────

/**
 * Employee submits an expense → notify the finance admin / approver.
 */
export async function notifyExpenseSubmitted(args: {
  actorId:    string;
  actorName:  string;
  approverId: string;
  expenseId:  string;
  amount:     number;
  currency:   string;
  category:   string;
}): Promise<SendResult> {
  return sendNotification({
    userId:     args.approverId,
    actorId:    args.actorId,
    type:       'expense_approved',   // reuse as generic expense type
    title:      `New expense submitted`,
    body:       `${args.actorName} submitted a ${args.category} expense for ${args.currency} ${args.amount.toFixed(2)}.`,
    entityType: 'expense',
    entityId:   args.expenseId,
    link:       '/finance/expense-approvals',
    priority:   'normal',
  });
}

/**
 * Finance admin approves/rejects an expense → notify the submitter.
 */
export async function notifyExpenseDecision(args: {
  actorId:    string;
  actorName:  string;
  employeeId: string;
  expenseId:  string;
  approved:   boolean;
  amount:     number;
  currency:   string;
}): Promise<SendResult> {
  return sendNotification({
    userId:     args.employeeId,
    actorId:    args.actorId,
    type:       args.approved ? 'expense_approved' : 'expense_rejected',
    title:      args.approved ? 'Expense approved' : 'Expense rejected',
    body:       `${args.currency} ${args.amount.toFixed(2)} expense ${args.approved ? 'approved' : 'rejected'} by ${args.actorName}.`,
    entityType: 'expense',
    entityId:   args.expenseId,
    link:       '/finance/expenses',
  });
}

/**
 * Notify a purchase-request submitter that their request was approved or
 * rejected. Mirrors notifyExpenseDecision for the Admin → Procurement flow.
 * Best-effort (sendNotification never throws); the caller guards on
 * requestorId existing and not being the actor.
 */
export async function notifyProcurementDecision(args: {
  actorId:       string;
  actorName:     string;
  requestorId:   string;
  requestId:     string;
  requestNumber: string;
  approved:      boolean;
}): Promise<SendResult> {
  return sendNotification({
    userId:     args.requestorId,
    actorId:    args.actorId,
    type:       args.approved ? 'procurement_approved' : 'procurement_rejected',
    title:      args.approved ? 'Purchase request approved' : 'Purchase request rejected',
    body:       `Request ${args.requestNumber} ${args.approved ? 'approved' : 'rejected'} by ${args.actorName}.`,
    entityType: 'procurement',
    entityId:   args.requestId,
    link:       '/admin/procurement',
  });
}

// ─── User invite requested (PulseBot Phase 4) ────────────────────────────────

/**
 * Notify every super_admin that a pending user invite was recorded so one of
 * them can complete provisioning in /admin/users. Best-effort: a notify failure
 * must never fail the invite itself (the caller ignores the result).
 */
export async function notifyUserInviteRequested(args: {
  requesterId:   string;
  inviteId:      string;
  email:         string;
  fullName:      string;
  suggestedRole: string;
}): Promise<SendResult> {
  const { data: admins, error: adminsError } = await supabase
    .from('profiles')
    .select('id')
    .eq('role', 'super_admin');
  /* super_admins are the ONLY recipients here — there is no fallback tier. A refused
   * read produced an empty list and the same silent `return` as a workspace with no
   * admins, so the invite sat in /admin/users with nobody told it existed. */
  if (adminsError) {
    logLookupFailure('notifyUserInviteRequested', 'super_admin recipients', adminsError);
    return { ok: false, error: RESOLUTION_FAILED };
  }
  const recipientIds = (admins ?? []).map((a: { id: string }) => a.id);
  if (recipientIds.length === 0) return { ok: false, error: RESOLUTION_FAILED };

  return sendNotificationToMany(recipientIds, {
    actorId:    args.requesterId,
    type:       'user_invite_requested',
    title:      'New user requested',
    body:       `${args.fullName} (${args.email}) — suggested ${args.suggestedRole}. Review in Users.`,
    entityType: 'user_invite',
    entityId:   args.inviteId,
    link:       '/admin/users',
    priority:   'normal',
  });
}

// ─── HR: Document signatures (sequential multi-sig — Stage 3) ─────────────────

/**
 * It is now `signerId`'s turn to sign `docId`. Fired (best-effort) by the lib
 * after a successful create-flow (signer #1) or each in-order sign (the next
 * signer). dedupeKey collapses repeats for the same (doc, order) slot so a
 * double-submit can't double-ping. Mirrors the single-recipient helpers.
 */
export async function notifyNextSigner(args: {
  signerId:     string;
  docId:        string;
  docName:      string;
  signingOrder: number;
  actorId?:     string;
}): Promise<SendResult> {
  if (!args.signerId) return { ok: true };
  return sendNotification({
    userId:     args.signerId,
    actorId:    args.actorId,
    type:       'document_signature_requested',
    title:      'Your signature is requested',
    body:       `"${args.docName}" needs your signature (step ${args.signingOrder}).`,
    entityType: 'employee_docs',
    entityId:   args.docId,
    link:       '/hr/my-hr',
    priority:   'high',
    dedupeKey:  `docsig:${args.docId}:${args.signingOrder}`,
  });
}

/**
 * All signers have signed — notify the doc owner + HR that the document is
 * fully executed. Recipient ids are resolved by the caller (owner + HR, deduped).
 */
export async function notifyDocFullyExecuted(args: {
  recipientIds: string[];
  docId:        string;
  docName:      string;
  actorId?:     string;
}): Promise<SendResult> {
  if (!args.recipientIds.length) return { ok: true };
  return sendNotificationToMany(args.recipientIds, {
    actorId:    args.actorId,
    type:       'document_fully_executed',
    title:      'Document fully executed',
    body:       `"${args.docName}" has been signed by all parties.`,
    entityType: 'employee_docs',
    entityId:   args.docId,
    link:       '/hr/documents',
  });
}

/**
 * A signer declined — the chain has halted. Notify the doc owner + HR.
 */
export async function notifyDocSignatureDeclined(args: {
  recipientIds: string[];
  docId:        string;
  docName:      string;
  declinerName: string;
  signingOrder: number;
}): Promise<SendResult> {
  if (!args.recipientIds.length) return { ok: true };
  return sendNotificationToMany(args.recipientIds, {
    type:       'document_signature_declined',
    title:      'Signature declined',
    body:       `${args.declinerName} declined to sign "${args.docName}" (step ${args.signingOrder}).`,
    entityType: 'employee_docs',
    entityId:   args.docId,
    link:       '/hr/documents',
    priority:   'high',
  });
}

// ─── Finance: Budget threshold ───────────────────────────────────────────────

/**
 * Roadmap §9 — "Alert at 80 percent budget used" + 100% over-spend.
 *
 * After an expense is approved, the caller passes the affected budget id and
 * this helper:
 *   1. Fetches the budget row + sums approved/paid expenses for that budget.
 *   2. Determines which threshold has been crossed (80% or 100%).
 *   3. Looks up the department head to receive the alert (if no head, falls
 *      back to all super_admins so the alert is never lost).
 *   4. De-dupes via localStorage so each threshold fires once per budget per
 *      browser session — re-crossing after spend is reduced will reset.
 *
 * A missing budget never blocks the approval flow — but a FAILED read is no
 * longer treated as a missing one. Returns `ok: false` whenever the alert may
 * not have reached the right people, so the caller can say so.
 */
export async function checkAndNotifyBudgetThreshold(args: {
  actorId:   string;
  actorName: string;
  budgetId:  string;
}): Promise<SendResult> {
  try {
    // 1. Fetch budget + total spend (approved + paid expenses, non-deleted).
    const [
      { data: budget,    error: budgetError },
      { data: spentRows, error: spentError  },
    ] = await Promise.all([
      supabase
        .from('budgets')
        .select('id, name, allocated_amount, currency, department_id, period_end')
        .eq('id', args.budgetId)
        .single(),
      supabase
        .from('expenses')
        .select('amount')
        .eq('budget_id', args.budgetId)
        .in('status', ['approved', 'paid'])
        .eq('is_deleted', false),
    ]);

    /* Both reads decide whether the alert fires AT ALL, so neither may be inferred
     * from an empty result:
     *   • budget — a failed read left `budget` null and hit the very same `return`
     *     as a budget that genuinely has no allocation. "Read refused" and "nothing
     *     configured" were one code path.
     *   • spend — worse, and the reason this function could go permanently quiet: a
     *     failed read summed [] to 0, so `pct` was 0, so no threshold was ever
     *     crossed and the function returned success-shaped silence. A budget 300%
     *     overspent raised nothing, and nothing anywhere recorded why. */
    if (budgetError) {
      logLookupFailure('checkAndNotifyBudgetThreshold', 'budget', budgetError);
      return { ok: false, error: budgetError.message };
    }
    if (spentError) {
      logLookupFailure('checkAndNotifyBudgetThreshold', 'budget spend', spentError);
      return { ok: false, error: spentError.message };
    }
    // Genuinely nothing to alert on: no budget row, or no allocation to exceed.
    if (!budget || !budget.allocated_amount) return { ok: true };

    const spent = (spentRows ?? []).reduce(
      (sum: number, e: { amount: number }) => sum + (e.amount ?? 0),
      0,
    );
    const pct = (spent / budget.allocated_amount) * 100;

    // 2. Determine crossed threshold (highest first wins).
    let threshold: 80 | 100 | null = null;
    if (pct >= 100)      threshold = 100;
    else if (pct >= 80)  threshold = 80;
    if (threshold == null) return { ok: true };   // under threshold is success, not failure

    // 3. Dedupe: only fire each threshold once per budget per browser.
    const dedupeKey = `pulsehq_budget_alert_${budget.id}_${threshold}`;
    if (typeof window !== 'undefined' && localStorage.getItem(dedupeKey)) return { ok: true };

    // 4. Resolve recipients — department head, or super_admins as fallback.
    let degraded = false;
    const recipients: string[] = [];
    if (budget.department_id) {
      const { data: dept, error: deptError } = await supabase
        .from('departments')
        .select('head_id')
        .eq('id', budget.department_id)
        .single();
      if (deptError) {
        /* Not fatal — the super_admin fallback below still gets the alert out. But it
         * is a DIFFERENT audience than the one configured, so the caller is told. */
        logLookupFailure('checkAndNotifyBudgetThreshold', 'department head', deptError);
        degraded = true;
      }
      if (dept?.head_id) recipients.push(dept.head_id);
    }
    if (recipients.length === 0) {
      const { data: admins, error: adminsError } = await supabase
        .from('profiles')
        .select('id')
        .eq('role', 'super_admin');
      if (adminsError) {
        // Last resort failed too, so there is nobody left to tell.
        logLookupFailure('checkAndNotifyBudgetThreshold', 'super_admin fallback', adminsError);
        return { ok: false, error: RESOLUTION_FAILED };
      }
      (admins ?? []).forEach((a: { id: string }) => recipients.push(a.id));
    }
    // An over-budget alert with no recipient is a lost alert, not a no-op.
    if (recipients.length === 0) return { ok: false, error: RESOLUTION_FAILED };

    // 5. Send the alert.
    const overrun = threshold === 100;
    const title   = overrun
      ? `Budget exceeded: ${budget.name}`
      : `Budget at ${Math.round(pct)}%: ${budget.name}`;
    const body    = overrun
      ? `${budget.currency} ${spent.toFixed(2)} spent of ${budget.allocated_amount.toFixed(2)} allocated. Action required.`
      : `${Math.round(pct)}% of ${budget.currency} ${budget.allocated_amount.toFixed(2)} used. Review remaining spend.`;

    const result = await sendNotificationToMany(recipients, {
      actorId:    args.actorId,
      type:       'budget_threshold',
      title,
      body,
      entityType: 'budget',
      entityId:   budget.id,
      link:       '/finance/budgets',
      priority:   overrun ? 'high' : 'normal',
    });

    /* Record the dedupe key only once the send has actually succeeded. It used to be
     * written back at step 3, BEFORE sending: a send that then failed left the key
     * behind, permanently suppressing that threshold on that browser. The alert could
     * never fire again for that budget — a one-off failure became a silent forever. */
    if (result.ok && typeof window !== 'undefined') localStorage.setItem(dedupeKey, '1');

    if (!result.ok) return result;
    return degraded ? { ok: false, error: RESOLUTION_FAILED } : { ok: true };
  } catch (err) {
    const e = err as { message?: string; code?: string; details?: string; hint?: string };
    logLookupFailure('checkAndNotifyBudgetThreshold', 'threshold check', e);
    return { ok: false, error: e?.message ?? 'Budget threshold check failed.' };
  }
}

// ─── Support: Ticket notifications ───────────────────────────────────────────

/**
 * Ticket created and assigned → notify the assignee.
 */
export async function notifyTicketAssigned(args: {
  actorId:    string;
  actorName:  string;
  assigneeId: string;
  ticketId:   string;
  subject:    string;
  priority:   string;
}): Promise<SendResult> {
  return sendNotification({
    userId:     args.assigneeId,
    actorId:    args.actorId,
    type:       'ticket_assigned',
    title:      `Ticket assigned to you`,
    body:       `"${args.subject}" [${args.priority}] from ${args.actorName}.`,
    entityType: 'ticket',
    entityId:   args.ticketId,
    link:       '/support/team-queue',
    priority:   args.priority === 'critical' ? 'critical' : args.priority === 'high' ? 'high' : 'normal',
  });
}

/**
 * Ticket resolved → notify the requester.
 */
export async function notifyTicketResolved(args: {
  actorId:     string;
  actorName:   string;
  requesterId: string;
  ticketId:    string;
  subject:     string;
}): Promise<SendResult> {
  return sendNotification({
    userId:     args.requesterId,
    actorId:    args.actorId,
    type:       'ticket_resolved',
    title:      `Your support ticket was resolved`,
    body:       `"${args.subject}" was resolved by ${args.actorName}.`,
    entityType: 'ticket',
    entityId:   args.ticketId,
    link:       '/support/my-tickets',
  });
}

// ─── Announcements ────────────────────────────────────────────────────────────

/**
 * New announcement broadcast → notify all provided user IDs.
 */
export async function notifyAnnouncement(args: {
  actorId:   string;
  actorName: string;
  userIds:   string[];
  title:     string;
  body?:     string;
}): Promise<SendResult> {
  return sendNotificationToMany(args.userIds, {
    actorId:    args.actorId,
    type:       'announcement',
    title:      args.title,
    body:       args.body,
    link:       '/announcements',
  });
}

// ─── KPI notifications ────────────────────────────────────────────────────────

/**
 * KPI assigned to employee → notify them.
 */
export async function notifyKPIAssigned(args: {
  actorId:    string;
  actorName:  string;
  employeeId: string;
  kpiId:      string;
  kpiName:    string;
}): Promise<SendResult> {
  return sendNotification({
    userId:     args.employeeId,
    actorId:    args.actorId,
    type:       'kpi_assigned',
    title:      `KPI assigned to you`,
    body:       `"${args.kpiName}" has been assigned by ${args.actorName}.`,
    entityType: 'kpi',
    entityId:   args.kpiId,
    link:       '/performance/my-kpis',
  });
}

// ─── HR: Compliance alerts ────────────────────────────────────────────────────

/**
 * Roadmap Module 3 — "Compliance Alerts auto-generated".
 *
 * Fires one in-app notification per (alert, recipient) pair, deduped per
 * browser per calendar day via localStorage so opening the Compliance page
 * twice on the same day doesn't double-ping. Server-side cron can replace
 * this when the FastAPI backend lands.
 *
 * The CompliancePage caller already computes the alerts; this helper just
 * routes them. Errors are swallowed so a network blip never breaks the page.
 */
export async function notifyComplianceAlerts(args: {
  alerts: Array<{
    /** Stable id from CompliancePage so dedupe is consistent across reloads. */
    id: string;
    type: 'probation' | 'contract' | 'document' | 'work_permit' | 'birthday' | 'anniversary';
    employeeName: string;
    detail: string;
    /** ISO date string of the deadline / event. */
    date: string;
    daysRemaining: number;
  }>;
  recipientIds: string[];
}): Promise<SendResult> {
  if (!args.alerts.length || !args.recipientIds.length) return { ok: true };
  if (typeof window === 'undefined') return { ok: true };
  const today = new Date().toISOString().split('T')[0];

  /* Reported back as one result for the whole batch: the first failure is carried out,
   * but every remaining alert is still attempted rather than abandoning the loop. */
  let firstError: string | undefined;

  for (const a of args.alerts) {
    // Cheap per-browser pre-check ONLY — skips a redundant DB round-trip on
    // rapid reloads in the same browser the same day. This is an optimization,
    // NOT the source of truth: correctness is enforced server-side by the
    // `dedupeKey` below (DB unique index), which holds across viewers and days.
    const localKey = `pulsehq_compliance_alert_${a.id}_${today}`;
    if (localStorage.getItem(localKey)) continue;

    // Stable, browser-independent idempotency key: one notification per
    // recipient per alert per event-date. Keyed on the alert id + the event
    // date (NOT "today"), so it does not re-fire daily over the alert window.
    const dedupeKey = `compliance:${a.id}:${a.date}`;

    const isUrgent = a.daysRemaining <= 7
      && a.type !== 'birthday'
      && a.type !== 'anniversary';
    const title =
      a.type === 'birthday'    ? `🎂 ${a.employeeName} — birthday today`
      : a.type === 'anniversary' ? `🏆 ${a.employeeName} — work anniversary`
      : `${a.detail}: ${a.employeeName}`;
    const body  =
      a.type === 'birthday' || a.type === 'anniversary'
        ? a.detail
        : a.daysRemaining === 0
          ? `Due today (${new Date(a.date).toLocaleDateString()})`
          : `Due in ${a.daysRemaining} day${a.daysRemaining === 1 ? '' : 's'} (${new Date(a.date).toLocaleDateString()})`;

    try {
      const result = await sendNotificationToMany(args.recipientIds, {
        type:       'compliance_alert',
        title,
        body,
        entityType: 'compliance_alert',
        entityId:   a.id,
        link:       '/hr/compliance',
        priority:   isUrgent ? 'high' : 'normal',
        dedupeKey,
      });
      /* Mark the cheap pre-check only after a send that actually SUCCEEDED, so a
       * transient failure doesn't suppress the next retry this session.
       *
       * That was the stated intent before, but it did not hold: sendNotificationToMany
       * RESOLVES on failure instead of throwing, so the catch below never ran on a
       * failed send and setItem executed anyway — suppressing the retry it was written
       * to protect. Gating on `result.ok` is the check that was always meant. */
      if (result.ok) {
        localStorage.setItem(localKey, '1');
      } else if (!firstError) {
        firstError = result.error;
      }
    } catch (err) {
      // Reached only on a genuine rejection (network/abort), not on a failed insert.
      const e = err as { message?: string; code?: string; details?: string; hint?: string };
      logLookupFailure('notifyComplianceAlerts', `alert ${a.id}`, e);
      if (!firstError) firstError = e?.message ?? 'Compliance alert send failed.';
    }
  }

  return firstError ? { ok: false, error: firstError } : { ok: true };
}

// ─── KPI: Status transition (at_risk / off_track) ────────────────────────────

/**
 * Roadmap §NOTIFICATION SYSTEM — "KPI at risk: team lead notified" /
 * "KPI off track: department head notified".
 *
 * Fires when a KPI transitions INTO `at_risk` or `off_track`. No notification
 * fires for transitions into `on_track` (good news doesn't need a ping) or
 * for repeat saves at the same status (would be spam).
 *
 * Recipients use the same 3-step fallback as `notifyKPIBlocker`:
 *   direct manager  →  department head  →  super_admins.
 *
 * Self-pings are dropped, body is truncated, priority scales with severity.
 */
export async function notifyKPIStatusChanged(args: {
  actorId:       string;
  actorName:     string;
  kpiId:         string;
  kpiName:       string;
  /** Optional — used for the second resolution step (departments.head_id). */
  departmentId?: string | null;
  /** Optional — assignee user_id, used for step 1 (employees.manager_id). */
  assignedTo?:   string | null;
  /** Status BEFORE this save. Skip the notification when prev === next. */
  previousStatus: 'on_track' | 'at_risk' | 'off_track';
  /** Status AFTER this save. */
  newStatus:      'on_track' | 'at_risk' | 'off_track';
}): Promise<SendResult> {
  // Only ping on transitions INTO a worse-than-on_track state.
  if (args.newStatus === args.previousStatus) return { ok: true };
  if (args.newStatus !== 'at_risk' && args.newStatus !== 'off_track') return { ok: true };

  try {
    const recipients: Set<string> = new Set();
    /* Each step below advances on `recipients.size === 0`, which a FAILED lookup
     * produces just as readily as a legitimately empty one. So a refused read at
     * step 1 silently escalates a single manager's KPI alert to every super_admin
     * at step 3 — and if step 3 is refused too, to nobody at all. */
    let degraded = false;

    // Step 1 — direct manager via profiles.manager_id (Fix 0.1: manager_id is a profile id).
    if (args.assignedTo) {
      const { data: emp, error: empError } = await supabase
        .from('profiles')
        .select('manager_id')
        .eq('id', args.assignedTo)
        .single();
      if (empError) {
        logLookupFailure('notifyKPIStatusChanged', 'assignee manager', empError);
        degraded = true;
      }
      if (emp?.manager_id) recipients.add(emp.manager_id);
    }

    // Step 2 — department head as a backup.
    if (recipients.size === 0 && args.departmentId) {
      const { data: dept, error: deptError } = await supabase
        .from('departments')
        .select('head_id')
        .eq('id', args.departmentId)
        .single();
      if (deptError) {
        logLookupFailure('notifyKPIStatusChanged', 'department head', deptError);
        degraded = true;
      }
      if (dept?.head_id) recipients.add(dept.head_id);
    }

    // Step 3 — super_admins last-resort.
    if (recipients.size === 0) {
      const { data: admins, error: adminsError } = await supabase
        .from('profiles')
        .select('id')
        .eq('role', 'super_admin');
      if (adminsError) {
        logLookupFailure('notifyKPIStatusChanged', 'super_admin fallback', adminsError);
        return { ok: false, error: RESOLUTION_FAILED };
      }
      (admins ?? []).forEach((a: { id: string }) => recipients.add(a.id));
    }

    recipients.delete(args.actorId);
    // Nobody left to tell. Only a clean cascade makes that a legitimate no-op.
    if (recipients.size === 0) return degraded ? { ok: false, error: RESOLUTION_FAILED } : { ok: true };

    const isOffTrack = args.newStatus === 'off_track';
    const emoji = isOffTrack ? '🔴' : '🟡';
    const label = isOffTrack ? 'Off track' : 'At risk';

    const result = await sendNotificationToMany(Array.from(recipients), {
      actorId:    args.actorId,
      type:       'kpi_updated',
      title:      `${emoji} KPI ${label}: "${args.kpiName}"`,
      body:       `${args.actorName} updated the KPI to ${label.toLowerCase()} status.`,
      entityType: 'kpi',
      entityId:   args.kpiId,
      link:       '/performance/kpi-tracker',
      priority:   isOffTrack ? 'high' : 'normal',
    });
    if (!result.ok) return result;
    return degraded ? { ok: false, error: RESOLUTION_FAILED } : { ok: true };
  } catch (err) {
    const e = err as { message?: string; code?: string; details?: string; hint?: string };
    logLookupFailure('notifyKPIStatusChanged', 'status-change notify', e);
    return { ok: false, error: e?.message ?? 'KPI status notification failed.' };
  }
}

// ─── Support: Ticket reply ────────────────────────────────────────────────────

/**
 * Roadmap §NOTIFICATION SYSTEM — "New reply: all participants notified".
 *
 * Called from `TicketDetailModal.postComment` after a `ticket_comments` insert.
 * Resolves the participant set (creator + assignee + previous commenters) and
 * fires one notification per recipient.
 *
 * Internal-note (`isInternal = true`) replies are scoped to **agents only**:
 * the ticket creator/requester is excluded so support staff can discuss the
 * case without leaking to the customer.
 *
 * Recipients to exclude:
 *  - the replier themselves
 *  - anyone already covered by `excludeIds` (typically the @mention recipients
 *    so the same user doesn't get a mention ping AND a reply ping for the
 *    same comment).
 */
export async function notifyTicketReply(args: {
  actorId:      string;
  actorName:    string;
  ticketId:     string;
  ticketTitle:  string;
  /** The user_id who created the ticket. */
  creatorId?:   string | null;
  /** The user_id of the currently assigned agent, if any. */
  assigneeId?:  string | null;
  isInternal:   boolean;
  /** Body text — used as a 140-char excerpt in the notification body. */
  body:         string;
  /** Already-notified recipients (e.g. @mentioned users) to skip. */
  excludeIds?:  string[];
}): Promise<SendResult> {
  try {
    const recipients = new Set<string>();
    if (args.creatorId  && !args.isInternal) recipients.add(args.creatorId);
    if (args.assigneeId)                     recipients.add(args.assigneeId);

    // Pull every unique commenter from the thread so all participants are looped in.
    const { data: prior, error: priorError } = await supabase
      .from('ticket_comments')
      .select('user_id, is_internal')
      .eq('ticket_id', args.ticketId)
      // Don't loop in authors whose only comment was soft-deleted (migration 0039
      // moved this gate out of the SELECT policy into the query).
      .eq('is_deleted', false);
    /* Unlike the cascades above this list is ADDITIVE, not a fallback — creator and
     * assignee are already in the set. So a refused read doesn't misroute anything, it
     * quietly truncates: every third-party participant drops out of the thread while
     * the reply still looks fully delivered. The send proceeds to whoever is known. */
    let degraded = false;
    if (priorError) {
      logLookupFailure('notifyTicketReply', 'thread participants', priorError);
      degraded = true;
    }
    (prior ?? []).forEach((c: { user_id: string; is_internal?: boolean }) => {
      // Skip the customer (creator) for internal threads.
      if (args.isInternal && c.user_id === args.creatorId) return;
      recipients.add(c.user_id);
    });

    // Drop self + already-notified.
    recipients.delete(args.actorId);
    (args.excludeIds ?? []).forEach(id => recipients.delete(id));
    if (recipients.size === 0) {
      return degraded
        ? { ok: false, error: 'Thread participants could not be read, so nobody was notified of the reply.' }
        : { ok: true };
    }

    const trimmed = args.body.length > 140
      ? args.body.slice(0, 137) + '…'
      : args.body;
    const internalTag = args.isInternal ? ' (internal)' : '';

    const result = await sendNotificationToMany(Array.from(recipients), {
      actorId:    args.actorId,
      type:       'ticket_reply',
      title:      `New reply${internalTag} on "${args.ticketTitle}"`,
      body:       `${args.actorName}: ${trimmed}`,
      entityType: 'ticket',
      entityId:   args.ticketId,
      link:       `/support/all-tickets?ticket=${args.ticketId}`,
    });
    if (!result.ok) return result;
    return degraded
      ? { ok: false, error: 'Some thread participants could not be resolved and were not notified.' }
      : { ok: true };
  } catch (err) {
    const e = err as { message?: string; code?: string; details?: string; hint?: string };
    logLookupFailure('notifyTicketReply', 'ticket reply notify', e);
    return { ok: false, error: e?.message ?? 'Ticket reply notification failed.' };
  }
}

// ─── Projects: Milestone reached ──────────────────────────────────────────────

/**
 * Roadmap §NOTIFICATION SYSTEM — "Project milestone: department head notified".
 *
 * Fires when a project transitions into `completed`. Recipients:
 *   1. The department head (`departments.head_id`) for the project's
 *      `department_id` — authoritative target per the roadmap.
 *   2. The project owner (`projects.owner_id`) so they always know,
 *      even if they aren't the actor.
 *   3. All super_admins as a last-resort fallback so the milestone is
 *      never lost (e.g. for a project with no department).
 *
 * Self-pings dropped. Priority is `high` because a project completion
 * is something a department head genuinely wants to see today.
 */
export async function notifyProjectMilestone(args: {
  actorId:       string;
  actorName:     string;
  projectId:     string;
  projectName:   string;
  departmentId?: string | null;
  ownerId?:      string | null;
}): Promise<SendResult> {
  try {
    const recipients: Set<string> = new Set();
    let degraded = false;

    if (args.departmentId) {
      const { data: dept, error: deptError } = await supabase
        .from('departments')
        .select('head_id')
        .eq('id', args.departmentId)
        .single();
      if (deptError) {
        logLookupFailure('notifyProjectMilestone', 'department head', deptError);
        degraded = true;
      }
      if (dept?.head_id) recipients.add(dept.head_id);
    }
    if (args.ownerId) recipients.add(args.ownerId);

    if (recipients.size === 0) {
      const { data: admins, error: adminsError } = await supabase
        .from('profiles')
        .select('id')
        .eq('role', 'super_admin');
      if (adminsError) {
        logLookupFailure('notifyProjectMilestone', 'super_admin fallback', adminsError);
        return { ok: false, error: RESOLUTION_FAILED };
      }
      (admins ?? []).forEach((a: { id: string }) => recipients.add(a.id));
    }

    recipients.delete(args.actorId);
    if (recipients.size === 0) return degraded ? { ok: false, error: RESOLUTION_FAILED } : { ok: true };

    const result = await sendNotificationToMany(Array.from(recipients), {
      actorId:    args.actorId,
      type:       'project_milestone',
      title:      `🎯 Project completed: "${args.projectName}"`,
      body:       `${args.actorName} marked the project as complete.`,
      entityType: 'project',
      entityId:   args.projectId,
      link:       `/work/projects/${args.projectId}`,
      priority:   'high',
    });
    if (!result.ok) return result;
    return degraded ? { ok: false, error: RESOLUTION_FAILED } : { ok: true };
  } catch (err) {
    const e = err as { message?: string; code?: string; details?: string; hint?: string };
    logLookupFailure('notifyProjectMilestone', 'milestone notify', e);
    return { ok: false, error: e?.message ?? 'Project milestone notification failed.' };
  }
}

// ─── KPI: Blocker flagged ─────────────────────────────────────────────────────

/**
 * Roadmap Module 2 — "Blocker flagged: manager notified immediately".
 *
 * Called from KPITrackerPage.submitComment / MyKPIsPage.submitComment whenever
 * a comment is inserted with `is_blocker = true`. Resolves the responsible
 * manager(s) and pings them.
 *
 * Manager resolution order (first non-empty wins):
 *   1. The KPI's `assigned_to` employee's `manager_id` from `employees`
 *      (looked up via the auth user_id link).
 *   2. The KPI's department head (`departments.head_id`).
 *   3. All super_admins as a last-resort fallback so the alert is never lost.
 *
 * Self-pings are silently dropped — flagging a blocker on a KPI you also
 * manage doesn't notify yourself.
 */
export async function notifyKPIBlocker(args: {
  actorId:      string;
  actorName:    string;
  kpiId:        string;
  kpiName:      string;
  /** Optional — used only for the second resolution step. */
  departmentId?: string | null;
  /** First-line comment text shown as the notification body. */
  comment:      string;
}): Promise<SendResult> {
  try {
    // Step 1 — try the KPI's assigned employee's manager.
    const recipients: Set<string> = new Set();
    /* Same cascade hazard as notifyKPIStatusChanged: every step advances on an empty
     * set, and a refused read is indistinguishable from an empty one. A blocker is
     * the highest-priority notification this module sends, so silently routing it to
     * the wrong desk — or none — is the worst case in the file. */
    let degraded = false;

    const { data: kpiRow, error: kpiError } = await supabase
      .from('kpis')
      .select('assigned_to')
      .eq('id', args.kpiId)
      .single();
    if (kpiError) {
      logLookupFailure('notifyKPIBlocker', 'KPI assignee', kpiError);
      degraded = true;
    }
    if (kpiRow?.assigned_to) {
      const { data: emp, error: empError } = await supabase
        .from('profiles')
        .select('manager_id')
        .eq('id', kpiRow.assigned_to)
        .single();
      if (empError) {
        logLookupFailure('notifyKPIBlocker', 'assignee manager', empError);
        degraded = true;
      }
      if (emp?.manager_id) recipients.add(emp.manager_id);
    }

    // Step 2 — department head as a backup.
    if (recipients.size === 0 && args.departmentId) {
      const { data: dept, error: deptError } = await supabase
        .from('departments')
        .select('head_id')
        .eq('id', args.departmentId)
        .single();
      if (deptError) {
        logLookupFailure('notifyKPIBlocker', 'department head', deptError);
        degraded = true;
      }
      if (dept?.head_id) recipients.add(dept.head_id);
    }

    // Step 3 — super_admins last-resort.
    if (recipients.size === 0) {
      const { data: admins, error: adminsError } = await supabase
        .from('profiles')
        .select('id')
        .eq('role', 'super_admin');
      if (adminsError) {
        logLookupFailure('notifyKPIBlocker', 'super_admin fallback', adminsError);
        return { ok: false, error: RESOLUTION_FAILED };
      }
      (admins ?? []).forEach((a: { id: string }) => recipients.add(a.id));
    }

    // Drop self.
    recipients.delete(args.actorId);
    if (recipients.size === 0) return degraded ? { ok: false, error: RESOLUTION_FAILED } : { ok: true };

    const trimmed = args.comment.length > 140
      ? args.comment.slice(0, 137) + '…'
      : args.comment;

    const result = await sendNotificationToMany(Array.from(recipients), {
      actorId:    args.actorId,
      type:       'kpi_blocker',
      title:      `🚩 Blocker flagged on "${args.kpiName}"`,
      body:       `${args.actorName} flagged a blocker: ${trimmed}`,
      entityType: 'kpi',
      entityId:   args.kpiId,
      link:       '/performance/kpi-tracker',
      priority:   'high',
    });
    if (!result.ok) return result;
    return degraded ? { ok: false, error: RESOLUTION_FAILED } : { ok: true };
  } catch (err) {
    const e = err as { message?: string; code?: string; details?: string; hint?: string };
    logLookupFailure('notifyKPIBlocker', 'blocker notify', e);
    return { ok: false, error: e?.message ?? 'KPI blocker notification failed.' };
  }
}

// ─── Mentions ─────────────────────────────────────────────────────────────────

/**
 * Parse `@FirstName` mentions in a free-text body and resolve them to user
 * IDs against the supplied known-users list. Matching is case-insensitive on
 * the first whitespace-delimited token of each user's `full_name`. Excludes
 * the author so a self-mention never fires a notification.
 *
 * Returned IDs are deduplicated.
 *
 * Why first-name only:
 *   The mention picker UIs in ChannelsPage and DirectMessagesPage insert
 *   `@${firstName}` after autocomplete selection, so the source-of-truth
 *   matches that convention. If two team members share a first name, the
 *   first match wins — clearly an edge case to revisit when @handles ship.
 */
export function parseMentions(
  body: string,
  candidates: { id: string; full_name?: string | null }[],
  excludeUserId?: string,
): string[] {
  if (!body) return [];
  const tokens = new Set<string>();
  const re = /@([A-Za-zÀ-ÖØ-öø-ÿ0-9_-]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    tokens.add(m[1].toLowerCase());
  }
  if (tokens.size === 0) return [];

  const ids = new Set<string>();
  for (const u of candidates) {
    if (!u.full_name) continue;
    if (excludeUserId && u.id === excludeUserId) continue;
    const first = u.full_name.split(/\s+/)[0]?.toLowerCase();
    if (first && tokens.has(first)) ids.add(u.id);
  }
  return Array.from(ids);
}

/**
 * Notify each mentioned user that they were tagged.
 * Used by Channels, DMs, and (future) comment threads.
 */
export async function notifyMentions(args: {
  actorId:     string;
  actorName:   string;
  recipientIds: string[];
  /** Free-text excerpt shown in the notification body (truncated). */
  excerpt:     string;
  /** Where clicking the notification should take the recipient. */
  link:        string;
  /** Optional source-record metadata so the bell can deep-link. */
  entityType?: string;
  entityId?:   string;
}): Promise<SendResult> {
  if (!args.recipientIds.length) return { ok: true };
  const trimmed = args.excerpt.length > 140
    ? args.excerpt.slice(0, 137) + '…'
    : args.excerpt;
  return sendNotificationToMany(args.recipientIds, {
    actorId:    args.actorId,
    type:       'mention',
    title:      `${args.actorName} mentioned you`,
    body:       trimmed,
    entityType: args.entityType,
    entityId:   args.entityId,
    link:       args.link,
  });
}
